import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import {
  AnnouncementRbacAuthorization,
  announcementReadPermission,
  announcementCreatePermission,
  announcementUpdatePermission,
  announcementDeletePermission,
  Announcement,
  CreateAnnouncementInput,
  UpdateAnnouncementInput,
} from '@internal/backstage-plugin-announcement-common';
import { AnnouncementApi, Permissions } from './AnnouncementApi';

export class AnnouncementClient implements AnnouncementApi {
  readonly #discoveryApi: DiscoveryApi;
  readonly #fetchApi: FetchApi;
  readonly #identityApi: IdentityApi;
  readonly #rbacAuth = new AnnouncementRbacAuthorization();

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    identityApi: IdentityApi;
  }) {
    this.#discoveryApi = options.discoveryApi;
    this.#fetchApi = options.fetchApi;
    this.#identityApi = options.identityApi;
  }

  private async baseUrl(): Promise<string> {
    return this.#discoveryApi.getBaseUrl('announcement');
  }

  private async getErrorMessage(resp: Response): Promise<string> {
    try {
      const body = await resp.json();
      return body?.error?.message ?? resp.statusText;
    } catch {
      return resp.statusText;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const { token = '' } = await this.#identityApi.getCredentials();
    return token !== '';
  }

  async checkPermissions(): Promise<Permissions> {
    const { token = '' } = await this.#identityApi.getCredentials();
    return {
      read: this.#rbacAuth.hasPermission(token, announcementReadPermission),
      create: this.#rbacAuth.hasPermission(token, announcementCreatePermission),
      update: this.#rbacAuth.hasPermission(token, announcementUpdatePermission),
      delete: this.#rbacAuth.hasPermission(token, announcementDeletePermission),
    };
  }

  async fetchPublicAnnouncements(): Promise<Announcement[]> {
    try {
      const url = `${await this.baseUrl()}/announcements/public`;
      // Use globalThis.fetch (plain browser fetch) to bypass the Backstage
      // IdentityAuthInjectorFetchMiddleware, which calls identityApi.getCredentials()
      // and blocks indefinitely before the user has signed in. The public
      // endpoint requires no authentication, so skipping the middleware is correct.
      const resp = await globalThis.fetch(url);
      if (!resp.ok) return [];
      const data = await resp.json();
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  async fetchActiveAnnouncements(): Promise<Announcement[]> {
    try {
      const url = `${await this.baseUrl()}/announcements/active`;
      const resp = await this.#fetchApi.fetch(url);
      if (!resp.ok) return [];
      const data = await resp.json();
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  async fetchAnnouncements(): Promise<Announcement[]> {
    const url = `${await this.baseUrl()}/announcements`;
    const resp = await this.#fetchApi.fetch(url);
    if (!resp.ok) {
      throw new Error(
        `Failed to fetch announcements: ${await this.getErrorMessage(resp)}`,
      );
    }
    const data = await resp.json();
    return Array.isArray(data) ? data : [];
  }

  async createAnnouncement(
    input: CreateAnnouncementInput,
  ): Promise<Announcement> {
    const url = `${await this.baseUrl()}/announcements`;
    const resp = await this.#fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!resp.ok) {
      throw new Error(
        `Failed to create announcement: ${await this.getErrorMessage(resp)}`,
      );
    }
    return resp.json();
  }

  async updateAnnouncement(
    id: string,
    input: UpdateAnnouncementInput,
  ): Promise<Announcement> {
    const url = `${await this.baseUrl()}/announcements/${id}`;
    const resp = await this.#fetchApi.fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!resp.ok) {
      throw new Error(
        `Failed to update announcement: ${await this.getErrorMessage(resp)}`,
      );
    }
    return resp.json();
  }

  async deleteAnnouncement(id: string): Promise<void> {
    const url = `${await this.baseUrl()}/announcements/${id}`;
    const resp = await this.#fetchApi.fetch(url, { method: 'DELETE' });
    if (!resp.ok) {
      throw new Error(
        `Failed to delete announcement: ${await this.getErrorMessage(resp)}`,
      );
    }
  }
}
